import { useEffect, useState } from "react";

/** Where the header starts condensing, in pixels of scroll. */
const THRESHOLD = 80;
/**
 * Where a condensed header expands again. Lower than THRESHOLD on
 * purpose: condensing shortens the document, which can pull the scroll
 * position back under THRESHOLD. Without this gap the header would flip
 * open, grow the page, cross THRESHOLD again and flicker in a loop.
 */
const RELEASE = 8;
/**
 * How much shorter the document gets when the header condenses (the
 * announcement bar plus the utility row), with a little slack. A page
 * that cannot scroll at least this far past THRESHOLD is left alone:
 * condensing it would clamp the scroll position below RELEASE and the
 * header would oscillate.
 */
const COLLAPSE_ROOM = 160;
/** The `md` breakpoint, the width at which the desktop nav row exists. */
const DESKTOP = "(min-width: 768px)";

export interface StickyHeaderState {
  /** Past the threshold at any width: the header tightens and lifts. */
  scrolled: boolean;
  /**
   * Past the threshold AND wide enough to have the desktop nav row, so the
   * utility row can fold away into a single bar. Deliberately false on
   * phones: down there the utility row carries the hamburger, and folding
   * it would leave no way to open the menu. It also gates `inert`, so the
   * folded row's links never stay tabbable while invisible.
   */
  condensed: boolean;
}

/** True when the page is tall enough to condense without clamping back. */
function roomToCondense(): boolean {
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  return maxScroll > THRESHOLD + COLLAPSE_ROOM;
}

/**
 * Scroll state for the sticky header. Scroll is read inside a rAF so a fast
 * flick schedules one state update per frame rather than one per event, and
 * the first read happens on mount so a restored or deep-linked scroll
 * position paints the condensed bar immediately instead of flashing the
 * tall one. The transition has hysteresis (THRESHOLD down, RELEASE up) and
 * only happens on pages with room to condense, so a short page never
 * flickers.
 */
export function useStickyHeader(): StickyHeaderState {
  const [state, setState] = useState<StickyHeaderState>({
    scrolled: false,
    condensed: false,
  });

  useEffect(() => {
    const media = window.matchMedia(DESKTOP);
    let frame = 0;
    const read = () => {
      frame = 0;
      const y = window.scrollY;
      setState((prev) => {
        const past = prev.scrolled
          ? y > RELEASE
          : y > THRESHOLD && roomToCondense();
        const condensed = past && media.matches;
        return prev.scrolled === past && prev.condensed === condensed
          ? prev
          : { scrolled: past, condensed };
      });
    };
    const onScroll = () => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(read);
      }
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    media.addEventListener("change", read);
    return () => {
      window.removeEventListener("scroll", onScroll);
      media.removeEventListener("change", read);
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return state;
}
